import type { Dispatch } from 'react';
import type { StoreApi } from 'zustand';

import { getTimeframeFromCalendarSelection } from './calendar.js';
import { focusTimeInput } from '../../../../forms/date-time-picker/utils/focusDateTimeInput.js';
import type { FormFieldMessagesReducerAction } from '../../../../forms/forms-core/state/formfield-messages-state-reducer.js';
import { removePrecisionFromTimeFrame } from '../../../../forms/forms-core/time/timeParser.js';
import { type Timeframe } from '../../../types/time.js';
import {
  isFullTimeframe,
  isTimeValue,
  mergeRangeValue,
} from '../../helpers.js';
import { validate } from '../../validation.js';
import type {
  DateTimeRangePickerTimeframeStates,
  DateTimeRangePickerTimeframeStoreActions,
} from '../dateTimeRangePicker.types.js';

/**
 * Creates actions for the DateTimeRangePicker store using the slice action handlers.
 *  @internal
 */
export function getDateTimeRangePickerActions(
  formFieldMessagesDispatch?: Dispatch<FormFieldMessagesReducerAction>,
  onChangeProp?: ((value: Timeframe | null) => void) | undefined,
) {
  const actions = (
    set: StoreApi<
      DateTimeRangePickerTimeframeStates &
        DateTimeRangePickerTimeframeStoreActions
    >['setState'],
  ): DateTimeRangePickerTimeframeStoreActions => {
    return {
      setCalendarValue: (inputValue) => {
        set((state) => {
          const calendarTimeframe = getTimeframeFromCalendarSelection(
            inputValue,
            state,
          );

          if (isFullTimeframe(calendarTimeframe)) {
            state.dateTimeRangePickerRef?.current?.close();
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                state.dateTimeRangePickerRef?.current?.focus?.();
                focusTimeInput(state.dateTimeFromRef);
              });
            });
            onChangeProp?.(calendarTimeframe);

            return {
              calendarValue: calendarTimeframe,
              dateTimeRangePickerTimeframe: calendarTimeframe,
            };
          }

          return { calendarValue: calendarTimeframe };
        });
      },
      setDateTimeRangePickerTimeframe: (value) => {
        return set((state) => {
          // Full clear: null resets both fields to null
          if (value === null) {
            onChangeProp?.(null);
            return {
              dateTimeRangePickerTimeframe: null,
              calendarValue: null,
            };
          }

          const merged = mergeRangeValue(
            state.dateTimeRangePickerTimeframe,
            value,
          );

          const timeframeWithPrecision =
            removePrecisionFromTimeFrame(merged, state.precision) ?? null;

          if (
            timeframeWithPrecision?.from === null &&
            timeframeWithPrecision?.to === null
          ) {
            onChangeProp?.(null);
          }

          isFullTimeframe(timeframeWithPrecision) &&
            onChangeProp?.(timeframeWithPrecision);

          const isPartialFrom =
            isTimeValue(merged.from) && merged.from.absoluteDate === '';
          const isPartialTo =
            isTimeValue(merged.to) && merged.to.absoluteDate === '';

          // Partial values while editing
          if (isPartialFrom || isPartialTo) {
            const errorKey = validate(
              timeframeWithPrecision,
              state.min,
              state.max,
              {
                formMessageDispatch: formFieldMessagesDispatch,
                formerError: state.dateTimeRangePickerError,
                precision: state.precision,
                showError: false,
                checkOrder: true,
                ref: state.dateTimeRangePickerRef,
                expectedFormat: state.formatString,
                valueMode: 'dateTimePicker',
                required: state.required,
              },
            );

            const calendarValue = {
              from: isPartialFrom ? null : timeframeWithPrecision?.from ?? null,
              to: isPartialTo ? null : timeframeWithPrecision?.to ?? null,
            };

            return {
              dateTimeRangePickerTimeframe: timeframeWithPrecision,
              calendarValue,
              calendarError: errorKey,
              dateTimeRangePickerError: errorKey,
            };
          }

          const dateTimeRangePickerError = validate(
            timeframeWithPrecision,
            state.min,
            state.max,
            {
              formMessageDispatch: formFieldMessagesDispatch,
              formerError: state.dateTimeRangePickerError,
              precision: state.precision,
              showError: false,
              checkOrder: true,
              ref: state.dateTimeRangePickerRef,
              expectedFormat: state.formatString,
              valueMode: 'dateTimePicker',
              required: state.required,
            },
          );

          return {
            dateTimeRangePickerTimeframe: timeframeWithPrecision,
            calendarValue: timeframeWithPrecision,
            calendarError: dateTimeRangePickerError,
            dateTimeRangePickerError,
          };
        });
      },
      clearCalendarValue: () => {
        set((state) => {
          if (
            !state.calendarValue ||
            (state.calendarValue.from && state.calendarValue.to)
          ) {
            return state;
          }

          if (
            state.dateTimeRangePickerTimeframe?.from &&
            state.dateTimeRangePickerTimeframe?.to
          ) {
            return { calendarValue: state.dateTimeRangePickerTimeframe };
          }

          // in case of a partial value, we want to clear the calendar selection entirely to avoid confusion
          return { calendarValue: null };
        });
      },
    };
  };

  return actions;
}
